import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Loader2, ArrowLeft } from "lucide-react";
import type ContactList from "@/model/interface/ContactList";

const contactListSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
});

type ContactListFormValues = z.infer<typeof contactListSchema>;

interface Props {
  initialData?: ContactList;
  onSubmit: (data: Partial<ContactList>) => Promise<void>;
  isSubmitting: boolean;
  submitLabel: string;
}

export default function ContactListForm({
  initialData,
  onSubmit,
  isSubmitting,
  submitLabel,
}: Props) {
  const navigate = useNavigate();

  const form = useForm<ContactListFormValues>({
    resolver: zodResolver(contactListSchema),
    defaultValues: {
      name: "",
      description: "",
    },
  });

  useEffect(() => {
    if (initialData) {
      form.reset({
        name: initialData.name ?? "",
        description: initialData.description ?? "",
      });
    }
  }, [initialData]);

  const handleSubmit = async (values: ContactListFormValues) => {
    await onSubmit({
      name: values.name,
      description: values.description || undefined,
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>List Details</CardTitle>
            <CardDescription>Create or update a contact list</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField control={form.control} name="name" render={({ field }) => (
              <FormItem>
                <FormLabel>Name *</FormLabel>
                <FormControl><Input {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <FormField control={form.control} name="description" render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl><Textarea rows={3} {...field} /></FormControl>
              </FormItem>
            )} />
          </CardContent>
        </Card>

        <div className="flex justify-between">
          <Button type="button" variant="ghost" onClick={() => navigate("/contacts/lists")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
}
